/**
 * Google Consent Mode v2.
 *
 * The GTM container (`GTM-TZGXRC`) is shared by every Mozio app and every
 * white-label, and it reads Consent Mode state. A container that expects that
 * state must get it from every app that loads it, otherwise the same tags run
 * against different assumptions depending on which app the visitor is in.
 *
 * Mirrors `apps/transfer-next/src/utils/google-analytics.ts` — keep the two in
 * sync.
 */

/**
 * Consent Mode signals, grouped by the Osano category that authorizes them.
 *
 * `analytics_storage` follows the ANALYTICS category; the three ad signals
 * follow MARKETING. Granting the ad signals on an analytics-only accept would
 * claim consent the visitor never gave.
 */
const ANALYTICS_SIGNALS = ["analytics_storage"] as const;
const MARKETING_SIGNALS = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
] as const;

const signalState = (
  signals: readonly string[],
  granted: boolean,
): Record<string, "granted" | "denied"> =>
  Object.fromEntries(
    signals.map((signal) => [signal, granted ? "granted" : "denied"]),
  );

let consentDefaultsInitialized = false;

/**
 * Seeds the dataLayer with denied-by-default consent.
 *
 * Idempotent. Runs in the startup sequence, before `setOsano` — which is what
 * injects the container — and again from `updateGoogleConsent`, so a
 * `consent update` can never reach the dataLayer ahead of the `consent default`
 * it refines.
 */
export const setGoogleConsent = () => {
  if (consentDefaultsInitialized) return;
  consentDefaultsInitialized = true;

  window.dataLayer = window.dataLayer ?? [];

  // Must push the `arguments` object, not a rest-parameter array: GTM
  // identifies gtag commands in the dataLayer by that shape, and a plain Array
  // is not equivalent. This mirrors Google's canonical snippet
  // `function gtag(){dataLayer.push(arguments)}` exactly.
  window.gtag =
    window.gtag ??
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      (window.dataLayer as unknown[]).push(arguments);
    };

  window.gtag("consent", "default", {
    ...signalState(ANALYTICS_SIGNALS, false),
    ...signalState(MARKETING_SIGNALS, false),
    wait_for_update: 500,
    url_passthrough: true,
  });
};

/** Pushes a Consent Mode update reflecting the visitor's Osano choices. */
export const updateGoogleConsent = ({
  analytics,
  marketing,
}: {
  analytics: boolean;
  marketing: boolean;
}) => {
  setGoogleConsent();

  window.gtag?.("consent", "update", {
    ...signalState(ANALYTICS_SIGNALS, analytics),
    ...signalState(MARKETING_SIGNALS, marketing),
  });
};
